export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type Theme = Exclude<ThemePreference, 'system'>;

/**
 * This device's copy of the account's color mode, kept only while dark mode is on for the account.
 * The boot script in `index.html.ejs` reads it to paint the theme before the app loads.
 */
export const THEME_STORAGE_KEY = 'albert-theme-preference';

export const isThemePreference = (value: unknown): value is ThemePreference =>
  THEME_PREFERENCES.some((preference) => preference === value);

export const readStoredThemePreference = (): ThemePreference | null => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : null;
  } catch {
    return null;
  }
};

/** Saves this device's copy, or removes it when there is nothing to keep. */
export const writeStoredThemePreference = (preference: ThemePreference | null) => {
  try {
    if (preference) {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } else {
      localStorage.removeItem(THEME_STORAGE_KEY);
    }
  } catch {
    // Storage is blocked (site data off, some LMS iframes): the theme still applies, just unsaved.
  }
};

export const clearStoredThemePreference = () => writeStoredThemePreference(null);
